/**
 * RhTransmettreArret — espace agent pour transmettre un arrêt de travail à la RH
 * (canal sécurisé). La pièce jointe (upload R2) pourra être ajoutée ; ici on
 * transmet déjà la période et le type, avec suivi du statut.
 */
import { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { ShieldCheck, Plus } from 'lucide-react';
import type { RhJustificatif } from '@roronoa-collectivites/shared-types';

const TYPES: Array<{ v: string; l: string }> = [
  { v: 'maladie', l: 'Arrêt maladie' },
  { v: 'accident_travail', l: 'Accident du travail' },
  { v: 'autre', l: 'Autre' },
];
const STATUT: Record<string, { l: string; v: 'success' | 'urgent' | 'neutral' }> = {
  en_attente: { l: 'En attente', v: 'urgent' },
  valide: { l: 'Validé', v: 'success' },
  refuse: { l: 'Refusé', v: 'neutral' },
};

export function RhTransmettreArret() {
  const today = new Date().toISOString().slice(0, 10);
  const [items, setItems] = useState<RhJustificatif[]>([]);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('maladie');
  const [debut, setDebut] = useState(today);
  const [fin, setFin] = useState(today);
  const [commentaire, setCommentaire] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function charger() {
    try { setItems(await api.rh.mesJustificatifs()); } catch { /* ignore */ }
  }
  useEffect(() => { void charger(); }, []);

  async function transmettre() {
    setSaving(true); setError(null); setMsg(null);
    try {
      await api.rh.transmettreJustificatif({ type, dateDebut: debut, dateFin: fin, commentaire });
      setMsg('Arrêt transmis à la RH.'); setOpen(false); setCommentaire(''); await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  return (
    <Card className="p-4">
      {error && <Alert variant="error" className="mb-3">{error}</Alert>}
      {msg && <Alert variant="success" className="mb-3">{msg}</Alert>}
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-grid h-8 w-8 place-items-center rounded-r-md bg-rh-tint text-rh-fg"><ShieldCheck className="h-4 w-4" /></span>
        <div className="flex-1">
          <h2 className="font-display text-sm font-semibold text-ink">Transmettre un arrêt de travail</h2>
          <p className="text-xs text-ink-subtle">Canal sécurisé : votre arrêt n'est visible que par la RH.</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Transmettre</Button>
      </div>
      {items.length > 0 && (
        <ul className="divide-y divide-border">
          {items.map((j) => (
            <li key={j.id} className="flex items-center gap-3 py-2 text-sm">
              <span className="text-ink">{TYPES.find((t) => t.v === j.type)?.l ?? j.type}</span>
              <span className="text-ink-muted">{new Date(`${j.dateDebut}T00:00:00`).toLocaleDateString('fr-FR')} → {new Date(`${j.dateFin}T00:00:00`).toLocaleDateString('fr-FR')}</span>
              <span className="ml-auto"><Badge variant={STATUT[j.statut]?.v ?? 'neutral'}>{STATUT[j.statut]?.l ?? j.statut}</Badge></span>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title="Transmettre un arrêt de travail"
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button><Button variant="rh" loading={saving} onClick={() => void transmettre()}>Transmettre</Button></>}>
        <div className="space-y-3">
          <Field label="Type" htmlFor="arr-type">
            <select id="arr-type" value={type} onChange={(e) => setType(e.target.value)} className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Du" htmlFor="arr-debut"><input id="arr-debut" type="date" value={debut} onChange={(e) => setDebut(e.target.value)} className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" /></Field>
            <Field label="Au" htmlFor="arr-fin"><input id="arr-fin" type="date" value={fin} onChange={(e) => setFin(e.target.value)} className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" /></Field>
          </div>
          <Field label="Commentaire (facultatif)" htmlFor="arr-com">
            <input id="arr-com" type="text" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink" />
          </Field>
          <p className="text-xs text-ink-subtle">La pièce justificative (photo / PDF de l'arrêt) pourra être jointe. En son absence, la RH pourra vous la demander ou la saisir manuellement.</p>
        </div>
      </Dialog>
    </Card>
  );
}
